import { CollapsableDraggableContainer, Divider, DynamicTextEditor, IDraggableElement } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import Constants from '../../../constants';
import { TranslationEditorContainer } from '../../../containers/translations/TranslationEditorContainer';
import ContentConstants from '../../../contentConstants';
import { IHasValidationModel, ISurveyEditModel, ITranslationEditItem } from '../../../models';
import { ValidationCollectionHelper } from '../../../utilities';
import { SurveyContentHelper } from '../../../utilities/SurveyContentHelper';
import { ClearDiv, Col3Col9, LabelAbove } from '../../common';
import { CollapsableSection } from '../../common/layouts/CollapsableSection';
import { SurveyDefinitions } from './SurveyDefinitions';
import SurveyInstruction from './SurveyInstruction';
import { SurveyPrivacyPolicy } from './SurveyPrivacyPolicy';
import { SurveyTitleIntroPropertiesUnderValidation } from './SurveyTitleIntroPropertiesUnderValidation';
import { useSelector } from 'react-redux';
import { DragEndEvent } from '@dnd-kit/core';

export interface ISurveyTitleIntroSectionProps extends IHasValidationModel {
	surveyEditModel: ISurveyEditModel;
	HandleSurveyTitleChange: (input: string) => void;
	HandleTranslationEditorTitleUpdate: (item: ITranslationEditItem) => void;
	HandleSurveyIntroChange: (input: string) => void;
	HandleSurveyInstructionsChange: (input: string) => void;
	HandleTranslationEditorIntroUpdate: (item: ITranslationEditItem) => void;
	HandleTranslationEditorInstructionsUpdate: (item: ITranslationEditItem) => void;
	HandleSurveyContentItemSortOrderChange: (newOrder: string) => void;
}

export function SurveyTitleIntroSection(props: ISurveyTitleIntroSectionProps) {
	const { validationResults, surveyEditModel } = props;
	const { openedModalPopupName } = useSelector((state: any) => state.surveyContentItems);
	const surveyPrivacy = (
		<div key='PrivacyPolicy'>
			<Divider type='dashed' />
			<SurveyPrivacyPolicy readOnly={false} saveInModal={true}></SurveyPrivacyPolicy>
		</div>
	);
	const surveyInstruction = (
		<div key='Instructions'>
			<SurveyInstruction></SurveyInstruction>
		</div>
	);

	const surveyDefinition = (
		<div key='Definition'>
			<Divider type='dashed' />
			<SurveyDefinitions readOnly={false} />
		</div>
	);

	const sortableItemsArr: IDraggableElement[] = SurveyContentHelper.getSortedElements(
		surveyEditModel.survey.contentItemsOrder,
		surveyPrivacy,
		surveyInstruction,
		surveyDefinition,
		false,
		openedModalPopupName,
	);

	const HandleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const newSortOrderStr = SurveyContentHelper.getNewSortedString(surveyEditModel.survey.contentItemsOrder, event);
			props.HandleSurveyContentItemSortOrderChange(newSortOrderStr);
		}
	};

	const { supportedCultures } = surveyEditModel;
	const validationHelper = new ValidationCollectionHelper<SurveyTitleIntroPropertiesUnderValidation>(validationResults);

	return (
		<CollapsableSection id={'survey-title-intro'} sectionHeader={ContentConstants.SECTIONHEADERS_SURVEY_GENERAL}>
			<Col3Col9
				label={ContentConstants.SUBSECTIONHEADERS_SURVEYTITLE}
				labelInfo={ContentConstants.INSTRUCTIONALTEXT_SURVEY_TITLE}
				labelInfoTitle={ContentConstants.SUBSECTIONHEADERS_SURVEYTITLE}
			>
				<LabelAbove type='input'>{ContentConstants.INPUTLABEL_SURVEY_TITLE}</LabelAbove>
				<DynamicTextEditor
					id='survey-title-editor'
					inputType='input'
					enableHtmlEditor={false}
					error={!validationHelper.IsValid(SurveyTitleIntroPropertiesUnderValidation.Title)}
					messages={validationHelper.Errors(SurveyTitleIntroPropertiesUnderValidation.Title)}
					placeholder={ContentConstants.SUBSECTIONHEADERS_SURVEYTITLE}
					onChange={props.HandleSurveyTitleChange}
					value={surveyEditModel.title.textValue}
				/>
				<ClearDiv clearHeight={'15px'} />
				{surveyEditModel.survey.surveyId !== 0 && (
					<TranslationEditorContainer
						enableHtmlEditor={false}
						property={'Title'}
						inputType={'input'}
						propertyDisplayName={ContentConstants.SUBSECTIONHEADERS_SURVEYTITLE}
						supportedCultures={supportedCultures}
						uniqueId={surveyEditModel.survey.surveyId}
						surveyId={surveyEditModel.survey.surveyId}
						defaultTranslation={surveyEditModel.title}
						notifyUpdate={props.HandleTranslationEditorTitleUpdate}
					/>
				)}
			</Col3Col9>
			<Divider type='dashed' />
			<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEYINTRO}>
				<LabelAbove type='input'>{ContentConstants.INPUTLABEL_SURVEY_INTRO}</LabelAbove>
				<DynamicTextEditor
					id='survey-intro-editor'
					inputType='textarea'
					enableHtmlEditor={true}
					placeholder={ContentConstants.SUBSECTIONHEADERS_SURVEYINTRO}
					onChange={props.HandleSurveyIntroChange}
					value={surveyEditModel.intro.textValue}
					htmlEditorMode={'custom'}
					toolbarConfiguration={Constants.customHtmlEditorOptions}
				/>
				<ClearDiv clearHeight={'15px'} />
				{surveyEditModel.survey.surveyId !== 0 && (
					<TranslationEditorContainer
						enableHtmlEditor={true}
						property={'Intro'}
						inputType={'textarea'}
						propertyDisplayName={ContentConstants.SUBSECTIONHEADERS_SURVEYINTRO}
						supportedCultures={supportedCultures}
						uniqueId={surveyEditModel.survey.surveyId}
						surveyId={surveyEditModel.survey.surveyId}
						defaultTranslation={surveyEditModel.intro}
						notifyUpdate={props.HandleTranslationEditorIntroUpdate}
					/>
				)}
			</Col3Col9>
			<div style={{ position: 'relative', zIndex: 1001 }}>
				<CollapsableDraggableContainer
					dragOverStyle={{ backgroundColor: '#EEE', position: 'relative', zIndex: -1 }}
					sortedElements={sortableItemsArr}
					handleDragEnd={HandleDragEnd}
					handleStyle={{ position: 'absolute', left: '100%' }}
				></CollapsableDraggableContainer>
			</div>
		</CollapsableSection>
	);
}
